// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import defaultStyles from '~/app/styles';
import { S, DARK } from '~/app/ui/tokens';
import uniqueId from 'lodash/uniqueId';
import _ from 'shared/lib/gettext';
import Line from '~/components/ProgressBar';

type Props = {
  classes: Object,
  progress: number,
  allMessages: string[],
  error: string
};

function ProgressTrack({
  progress, classes: c, allMessages, error
}: Props) {
  const containerStyle = {
    width: '400px',
    height: '10px',
    margin: 'auto'
  };
  const animatedDots = (
    <span className={c.animatedDots}>
      {[...Array(3).keys()].map(i => <span key={i}>.</span>)}
    </span>
  );

  // If deployment completes successfully (progress >= 1), don't show error
  // This handles cases where the GraphQL HTTP request times out but
  // the deployment continues via WebSocket and eventually succeeds
  const isSuccess = progress >= 1;
  const showError = error && !isSuccess;

  return (
    <div className={c.container}>
      <div className={c.title}>
        {showError && <span className={c.titleError}>{_('ERROR')}</span>}
        {!showError && !isSuccess && <span>{_('DEPLOYING')}{animatedDots}</span>}
        {isSuccess && <span className={c.titleSuccess}>{_('SUCCESS')}</span>}
      </div>
      <div className={c.progress}>Progress:&nbsp;
        <span className={c.percent}>{`${Math.round(progress * 100)} %`}</span>
      </div>
      <Line
        progress={progress}
        containerStyle={containerStyle}
      />
      {
        showError && (
          <div className={c.error}>
            {_('ERROR_FAILED_DEPPLOYMENT')}
            <div className={c.errorMsg}>{error}</div>
          </div>
        )
      }
      <TransitionGroup component={null}>
        <CSSTransition
          appear
          key="messages"
          classNames="transition"
          timeout={{ appear: 500 }}
        >
          <div className={c.historyMsg}>
            {allMessages && allMessages.map(m => <p key={uniqueId()}>{m}</p>)}
          </div>
        </CSSTransition>
      </TransitionGroup>
      <div className={c.background} />
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    '& svg': {
      height: '10px',
      width: '400px !important',
      margin: 'auto'
    },
    '& path': {
      stroke: props => (props.error && props.progress < 1 ? 'hsl(0, 34%, 48%)' : defaultStyles.COLOR_PRIMARY)
    }
  },
  title: {
    textAlign: 'center',
    fontSize: '22px',
    marginBottom: '20px',
    height: '27px',
    lineHeight: '27px'
  },
  titleError: {
    color: 'hsl(0, 34%, 48%)',
    [DARK]: { color: S.red }
  },
  titleSuccess: {
    color: defaultStyles.COLOR_PRIMARY
  },
  background: {
    position: 'absolute',
    backgroundColor: '#ddd',
    margin: 'auto',
    zIndex: '-1',
    width: '400px',
    height: '10px',
    left: '0',
    right: '0',
    top: '81px',
    [DARK]: { backgroundColor: S.chromeHover }
  },
  percent: {
    fontSize: '20px',
    fontWeight: '600',
    color: props => (props.error && props.progress < 1 ? 'hsl(0, 34%, 48%)' : defaultStyles.COLOR_PRIMARY)
  },
  progress: {
    marginBottom: '10px'
  },
  animatedDots: {
    fontSize: '40px',
    '& span': {
      animationName: '$blink',
      animationDuration: '1.4s',
      animationIterationCount: 'infinite',
      animationFillMode: 'both'
    },
    '& span:nth-child(2)': {
      animationDelay: '.2s',
    },
    '& span:last-child': {
      animationDelay: '.4s',
    }
  },
  '@keyframes blink': {
    '0%': { opacity: '.2' },
    '20%': { opacity: '1' },
    '100%': { opacity: '.2' }
  },
  error: {
    marginTop: '10px',
    fontWeight: '600',
    color: 'hsl(0, 34%, 48%)',
    [DARK]: { color: S.red }
  },
  errorMsg: {
    color: 'black',
    marginTop: '10px',
    [DARK]: { color: S.fg }
  },
  message: {
    marginTop: '10px'
  },
  historyMsg: {
    color: '#ccc',
    marginBottom: '10px',
    '&.transition-appear': {
      opacity: '0.01'
    },
    '&.transition-appear&.transition-appear-active': {
      opacity: '1',
      transition: 'opacity 300ms ease-in 200ms'
    },
    '& > p': {
      textDecoration: 'line-through'
    },
    '& > p:first-child': {
      color: 'black',
      textDecoration: 'none'
    },
    [DARK]: { color: S.muted, '& > p:first-child': { color: S.fg } }
  }
};

export default injectSheet(styles)(ProgressTrack);
